<template>
  <template v-if="column === 'name'">
    <span v-if="!model.isDraft">{{ model.name }}</span>
    <div v-else>
      <n-text-input
        v-model="model.name"
        :hideLabel="true"
        label="Service Name"
        :errorMessage="getErrorMessage('name', 'Service Name')"
      ></n-text-input>
    </div>
  </template>

  <template v-else-if="column === 'phoneNumber'">
    <span v-if="!model.isDraft">{{ model.phoneNumber }}</span>
    <div v-else>
      <n-text-input
        v-model="model.phoneNumber"
        :hideLabel="true"
        label="Phone Number"
        :errorMessage="getErrorMessage('phoneNumber', 'Phone Number')"
      ></n-text-input>
    </div>
  </template>

  <template v-else-if="column === 'notifyApiKey'">
    <span v-if="!model.isDraft">*****</span>
    <div v-else>
      <n-text-input
        v-model="model.notifyApiKey"
        :hideLabel="true"
        label="API Key"
        @change="loadTemplates"
        :errorMessage="getErrorMessage('notifyApiKey', 'API Key')"
      ></n-text-input>
    </div>
  </template>

  <template v-else-if="column === 'templateName'">
    <span v-if="!model.isDraft">{{ model.templateName }}</span>
    <div v-else>
      <n-split-input-button :hideLabel="true" label="Template Name" :id="`templateName-${model.id}`">
        <template v-slot:input>
          <n-select
            :id="`templateName-${model.id}`"
            v-model="model.selectedTemplate"
            :options="getTemplateOptions"
            value-prop="id"
            label-prop="name"
            :hideLabel="true"
            :disabled="!canLoadTemplates"
            :errorMessage="getErrorMessage('selectedTemplate', 'Template')"
          ></n-select>
        </template>
        <template v-slot:button>
          <n-button icon="refresh" variant="split" :disabled="!canLoadTemplates" @click="loadTemplates"></n-button>
        </template>
      </n-split-input-button>
    </div>
  </template>

  <template v-else-if="column === 'bearerToken'">
    <span v-if="!model.isDraft">*****</span>
    <div v-else>
      <n-readonly-input
        :value="model.bearerToken"
        label="Bearer Token"
        @copy="onCopy"
        :hideLabel="true"
      ></n-readonly-input>
    </div>
  </template>

  <template v-else-if="column === 'actions'">
    <n-button variant="action" icon="delete" @click="handleDeleteClick"></n-button>
  </template>
</template>

<script lang="ts" setup>
import Toast from '@/shared/services/toast';
import { maxLength, required, requiredIf, numeric } from '@vuelidate/validators';
import { GovNotifyServiceViewModel } from '../models/govNotifyServiceViewModel';
import { govNotifyApi } from '../services/govNotifyApi';
import { computed, PropType, ref, watch } from 'vue';
import useVuelidate from '@vuelidate/core';
import VuelidateUtil from '@shared/util/vuelidateUtil';
import NSplitInputButton from '@shared/designsystem/components/input/split-input-button/split-input-button.vue';

const props = defineProps({
  modelValue: {
    type: Object as PropType<Partial<GovNotifyServiceViewModel>>,
    required: true,
  },
  loadingTemplates: {
    type: Boolean,
    default: false,
  },
  workspaceId: {
    type: String,
    required: true,
  },
  column: {
    type: String as PropType<'name' | 'phoneNumber' | 'notifyApiKey' | 'templateName' | 'bearerToken' | 'actions'>,
    required: true,
  },
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: Partial<GovNotifyServiceViewModel>): void;
  (e: 'update:loadingTemplates', loading: boolean): void;
  (e: 'delete', row: Partial<GovNotifyServiceViewModel>): void;
  (e: 'copy'): void;
}>();

const model = ref(props.modelValue);

const validations = {
  name: { required },
  name: { required },
  phoneNumber: {
    required: requiredIf(() => !!model.value.isDraft),
    numeric: numeric,
    maxLength: maxLength(15),
  },
  notifyApiKey: {
    required: requiredIf(() => !!model.value.isDraft),
  },
  selectedTemplate: {
    required: requiredIf(() => !!model.value.isDraft),
  },
};

const $v = useVuelidate(validations, model.value);

async function loadTemplates() {
  if (model.value.notifyApiKey === '') {
    return;
  }
  emit('update:loadingTemplates', true);
  try {
    const response = await govNotifyApi.getTemplates(props.workspaceId, model.value.notifyApiKey);
    model.value.templates = response.templates;
    model.value.selectedTemplate = response.templates[0] || { id: '', name: 'No templates configured', body: '' };
  } catch (err: any) {
    Toast.showError(err.message);
  }
  emit('update:loadingTemplates', false);
}

const getTemplateOptions = computed(() => {
  if (!model.value.templates) {
    model.value.selectedTemplate = { id: '', name: 'No templates configured', body: '' };
    return [model.value.selectedTemplate];
  }
  return model.value.templates;
});

const canLoadTemplates = computed(() => {
  return !props.loadingTemplates && model.value.notifyApiKey.length > 0;
});

watch(
  () => model.value.name,
  () => {
    $v.value.name.$touch();
    emit('update:modelValue', model.value);
  },
);
watch(
  () => model.value.phoneNumber,
  () => {
    $v.value.phoneNumber.$touch();
    emit('update:modelValue', model.value);
  },
);
watch(
  () => model.value.notifyApiKey,
  () => {
    $v.value.notifyApiKey.$touch();
    emit('update:modelValue', model.value);
  },
);
watch(
  () => model.value.selectedTemplate,
  () => {
    $v.value.selectedTemplate.$touch();
    emit('update:modelValue', model.value);
  },
  { immediate: true },
);

function getErrorMessage(field: string, label: string) {
  return VuelidateUtil.getErrorMessage($v, field, label);
}

function onCopy() {
  emit('copy');
  Toast.showInfo('Copied to clipboard');
}

function handleDeleteClick() {
  emit('delete', model.value);
}
</script>
